<template>
    <!-- Tarjeta con el diseño de Mi cuenta (estilos en bkAuth.css / bkCuenta.css). -->
    <section class="bk-card" aria-labelledby="vw-title">
        <header class="bk-card-head">
            <span class="bk-auth-wsp" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2 22l5.25-1.38a9.9 9.9 0 0 0 4.79 1.22h.01c5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.9-7.01A9.82 9.82 0 0 0 12.04 2Zm0 18.15h-.01a8.2 8.2 0 0 1-4.19-1.15l-.3-.18-3.12.82.83-3.04-.2-.31a8.18 8.18 0 0 1-1.26-4.38c0-4.54 3.7-8.23 8.25-8.23 2.2 0 4.27.86 5.83 2.41a8.18 8.18 0 0 1 2.41 5.83c0 4.54-3.7 8.23-8.24 8.23Zm4.52-6.16c-.25-.12-1.47-.72-1.69-.81-.23-.08-.39-.12-.56.13-.16.24-.64.8-.78.97-.15.16-.29.18-.54.06-.25-.13-1.05-.39-1.99-1.23-.74-.66-1.23-1.47-1.38-1.72-.14-.25-.01-.38.11-.5.11-.11.25-.29.37-.43.12-.15.16-.25.25-.41.08-.17.04-.31-.02-.43-.06-.12-.56-1.34-.76-1.84-.2-.48-.4-.42-.56-.43h-.48c-.17 0-.43.06-.66.31-.23.25-.86.85-.86 2.06s.89 2.39 1.01 2.56c.12.16 1.75 2.67 4.24 3.74.59.26 1.05.41 1.41.52.59.19 1.13.16 1.56.1.48-.07 1.47-.6 1.68-1.18.21-.58.21-1.08.14-1.18-.06-.11-.22-.17-.47-.29Z"/>
                </svg>
            </span>
            <div>
                <h2 id="vw-title" class="bk-card-title">{{ $t('register-new.verify-wsp.title') }}</h2>
                <p class="bk-card-desc">
                    {{ $t('register-new.verify-wsp.sent-to') }}
                    <span class="bk-auth-phone">+{{ String(telefono).replace(/^\+/, '') }}</span>.
                    {{ $t('register-new.verify-wsp.instructions') }}
                </p>
            </div>
        </header>

        <!-- Código de 6 dígitos -->
        <div :class="['bk-auth-code', { 'is-invalid': !!error }]" role="group" :aria-label="$t('register-new.verify-wsp.code-label')" @paste.prevent="onPaste">
            <input
                v-for="(d, i) in digitos"
                :key="i"
                :ref="'digito' + i"
                :value="digitos[i]"
                type="tel"
                inputmode="numeric"
                maxlength="1"
                autocomplete="one-time-code"
                :disabled="isLoading || verificado"
                :aria-label="`${$t('register-new.verify-wsp.code-label')} ${i + 1}/6`"
                :aria-invalid="error ? 'true' : 'false'"
                @input="onInput(i, $event)"
                @keydown.delete="onBackspace(i, $event)"
                @keydown.left.prevent="focusDigito(i - 1)"
                @keydown.right.prevent="focusDigito(i + 1)"
                @focus="$event.target.select()"
            />
        </div>

        <p v-if="error" class="bk-auth-msg is-error" role="alert">{{ error }}</p>
        <p v-else-if="aviso" class="bk-auth-msg is-ok" role="status">{{ aviso }}</p>

        <button
            class="bk-btn bk-btn--primary bk-auth-submit"
            type="button"
            :disabled="!codigoCompleto || isLoading || verificado"
            @click="verificar"
        >
            <span v-if="isLoading" class="bk-spinner" aria-hidden="true"></span>
            {{ isLoading ? $t('register-new.verify-wsp.verifying') : $t('register-new.verify-wsp.verify') }}
        </button>

        <p class="bk-auth-resend" aria-live="polite">
            <span v-if="segundosParaReenvio > 0">
                {{ $t('register-new.verify-wsp.resend-in', { seconds: segundosParaReenvio }) }}
            </span>
            <button v-else type="button" class="bk-auth-link" :disabled="reenviando" @click="reenviar">
                {{ reenviando ? $t('register-new.verify-wsp.resending') : $t('register-new.verify-wsp.resend') }}
            </button>
        </p>
    </section>
</template>

<script>

import { mapActions } from "vuex";

/**
 * Paso intermedio del registro: el usuario ya está creado, pero no avanza al
 * resto del wizard hasta confirmar el código que le llega por WhatsApp.
 *
 * El código lo genera la API y se lo pasa al bot de n8n, que sólo manda el
 * WhatsApp. Desde acá nunca se ve: se manda a comparar y la API contesta si
 * coincide.
 */
export default {
    name: "VerificarWhatsapp",
    props: {
        telefono: {
            type: String,
            required: true
        },
        /**
         * Segundos que hay que esperar para poder pedir otro código. Coincide
         * con el mínimo que exige la API (User::CODIGO_WSP_ESPERA_REENVIO).
         */
        esperaReenvio: {
            type: Number,
            default: 60
        }
    },
    data() {
        return {
            digitos: ['', '', '', '', '', ''],
            isLoading: false,
            reenviando: false,
            verificado: false,
            error: '',
            aviso: '',
            segundosParaReenvio: 0,
            timer: null
        };
    },
    computed: {
        codigo() {
            return this.digitos.join('');
        },
        codigoCompleto() {
            return this.codigo.length === 6;
        }
    },
    methods: {

        ...mapActions([
            "enviarCodigoWsp",
            "verificarCodigoWsp"
        ]),

        focusDigito(i) {
            if (i < 0 || i > 5) return;
            const ref = this.$refs['digito' + i];
            const input = Array.isArray(ref) ? ref[0] : ref;
            if (input) input.focus();
        },

        onInput(i, event) {
            const soloDigitos = (event.target.value || '').replace(/\D/g, '');
            const digito = soloDigitos.slice(-1);
            this.$set(this.digitos, i, digito);
            // El input no está con v-model, así que si lo tipeado se descartó
            // (una letra, por ejemplo) hay que sacarlo del DOM a mano: el
            // binding no vuelve a pintar cuando el valor no cambió.
            event.target.value = digito;
            this.error = '';

            if (this.digitos[i] && i < 5) {
                this.focusDigito(i + 1);
            }

            if (this.codigoCompleto) {
                this.verificar();
            }
        },

        onBackspace(i, event) {
            // Con la casilla ya vacía, el borrar tiene que llevarte a la anterior.
            if (!this.digitos[i] && i > 0) {
                event.preventDefault();
                this.$set(this.digitos, i - 1, '');
                this.focusDigito(i - 1);
            }
        },

        onPaste(event) {
            const texto = (event.clipboardData || window.clipboardData).getData('text') || '';
            const soloDigitos = texto.replace(/\D/g, '').slice(0, 6);
            if (!soloDigitos) return;

            for (let i = 0; i < 6; i++) {
                this.$set(this.digitos, i, soloDigitos[i] || '');
            }
            this.error = '';
            this.focusDigito(Math.min(soloDigitos.length, 5));

            if (this.codigoCompleto) {
                this.verificar();
            }
        },

        limpiarCodigo() {
            this.digitos = ['', '', '', '', '', ''];
            this.$nextTick(() => this.focusDigito(0));
        },

        async verificar() {
            if (!this.codigoCompleto || this.isLoading || this.verificado) return;

            this.isLoading = true;
            this.error = '';
            this.aviso = '';

            try {
                await this.verificarCodigoWsp(this.codigo);
                this.verificado = true;
                this.detenerTimer();
                this.$emit('verificado');
            } catch (e) {
                this.mostrarErrorDeVerificacion(e);
                this.limpiarCodigo();
            } finally {
                this.isLoading = false;
            }
        },

        mostrarErrorDeVerificacion(e) {
            const data = (e && e.response && e.response.data) || {};

            switch (data.motivo) {
                case 'sin_codigo':
                    this.error = this.$t('register-new.verify-wsp.error-no-code');
                    this.segundosParaReenvio = 0;
                    break;
                case 'vencido':
                    this.error = this.$t('register-new.verify-wsp.error-expired');
                    this.segundosParaReenvio = 0;
                    break;
                case 'demasiados_intentos':
                    this.error = this.$t('register-new.verify-wsp.error-too-many');
                    this.segundosParaReenvio = 0;
                    break;
                case 'incorrecto':
                    this.error = data.intentos_restantes > 0
                        ? this.$t('register-new.verify-wsp.error-incorrect-attempts', { attempts: data.intentos_restantes })
                        : this.$t('register-new.verify-wsp.error-incorrect');
                    break;
                default:
                    this.error = this.$t('register-new.verify-wsp.error-generic');
            }
        },

        async reenviar() {
            if (this.reenviando || this.segundosParaReenvio > 0) return;

            this.reenviando = true;
            this.error = '';
            this.aviso = '';

            try {
                await this.enviarCodigoWsp();
                this.aviso = this.$t('register-new.verify-wsp.resent');
                this.limpiarCodigo();
                this.arrancarTimer(this.esperaReenvio);
            } catch (e) {
                const data = (e && e.response && e.response.data) || {};
                if (data.motivo === 'espera') {
                    // La API es la que manda: si todavía falta, se respeta su cuenta.
                    this.arrancarTimer(data.segundos_restantes || this.esperaReenvio);
                } else {
                    this.error = this.$t('register-new.verify-wsp.error-send');
                }
            } finally {
                this.reenviando = false;
            }
        },

        arrancarTimer(segundos) {
            this.detenerTimer();
            this.segundosParaReenvio = segundos;
            this.timer = setInterval(() => {
                this.segundosParaReenvio--;
                if (this.segundosParaReenvio <= 0) {
                    this.detenerTimer();
                }
            }, 1000);
        },

        detenerTimer() {
            if (this.timer) {
                clearInterval(this.timer);
                this.timer = null;
            }
        }
    },
    mounted() {
        // El alta ya disparó el envío, así que la espera arranca corriendo.
        this.arrancarTimer(this.esperaReenvio);
        this.focusDigito(0);
    },
    beforeDestroy() {
        this.detenerTimer();
    }
};

</script>
